import {
  collectAssemblyNodeIdentityLookupIds,
  normalizeAssemblyNodeId,
} from '../../assembly/utils/AssemblyNodePath'
import type { ModelAssetNode } from '../assets/model-asset-types'

const PROJECT_MODEL_RUNTIME_OCCURRENCE_SCOPE = '::project-occurrence:'

/**
 * 规范化项目运行时 occurrence 身份。
 *
 * @param value 外部文档、hierarchy 或运行时 metadata 提供的候选身份。
 * @returns 可安全比较和拼接的节点 ID；非字符串返回空字符串。
 */
export function normalizeProjectModelRuntimeNodeId(value: unknown): string {
  return typeof value === 'string' ? normalizeAssemblyNodeId(value).trim() : ''
}

/**
 * 判断两个项目 occurrence 身份在规范化与安全解码后是否等价。
 *
 * @param left 左侧节点身份。
 * @param right 右侧节点身份。
 * @returns 任一稳定查找形式相同时返回 true。
 */
export function areProjectModelRuntimeNodeIdsEquivalent(left: string, right: string): boolean {
  const leftLookupIds = new Set(collectAssemblyNodeIdentityLookupIds(
    normalizeProjectModelRuntimeNodeId(left),
  ))
  return collectAssemblyNodeIdentityLookupIds(normalizeProjectModelRuntimeNodeId(right))
    .some((lookupId) => leftLookupIds.has(lookupId))
}

/**
 * 判断项目 occurrence 是否等于指定根，或位于该根的后代路径中。
 *
 * @param nodeId 待判断节点身份。
 * @param rootNodeId 子树根身份。
 * @returns 节点属于该子树时返回 true。
 */
export function isProjectModelRuntimeNodeIdInside(nodeId: string, rootNodeId: string): boolean {
  const nodeLookupIds = collectAssemblyNodeIdentityLookupIds(
    normalizeProjectModelRuntimeNodeId(nodeId),
  )
  const rootLookupIds = collectAssemblyNodeIdentityLookupIds(
    normalizeProjectModelRuntimeNodeId(rootNodeId),
  )
  return nodeLookupIds.some((nodeLookupId) => rootLookupIds.some((rootLookupId) => (
    nodeLookupId === rootLookupId || nodeLookupId.startsWith(`${rootLookupId}/`)
  )))
}

/**
 * 创建从离线包 occurrence 物化出的稳定项目节点 key。
 *
 * 设计说明：显示名称和兄弟序号都可能因重命名、删除或排序变化而改变，不能进入项目节点身份。
 * `occurrenceId` 是 V6 `project-model.json` 明确定义的文档内稳定身份，因此结构 key 只从父 key
 * 和 occurrenceId 派生，保证删除任意兄弟后其余节点的缓存、场景绑定和工作文档索引不漂移。
 *
 * @param parentNodeKey 父项目节点的稳定 key。
 * @param occurrenceId V6 项目文档内的稳定 occurrence ID。
 * @returns 可用于 ModelAssetNode 的稳定结构 key。
 */
export function createMaterializedProjectModelNodeKey(
  parentNodeKey: string,
  occurrenceId: string,
): string {
  const normalizedParentNodeKey = parentNodeKey.trim().replace(/\/+$/u, '')
  const normalizedOccurrenceId = occurrenceId.trim()
  if (!normalizedParentNodeKey || !normalizedOccurrenceId) {
    throw new Error(
      `PuppyCAD 项目 occurrence 无法创建稳定节点 key：parentNodeKey=${parentNodeKey}, occurrenceId=${occurrenceId}`,
    )
  }
  return `${normalizedParentNodeKey}/${encodeURIComponent(normalizedOccurrenceId)}`
}

/**
 * 解析项目节点在选择、变换、拓扑和持久化链路中的权威装配身份。
 *
 * 设计说明：
 * 1. 运行树显式 `assemblyNodeId` 拥有最高优先级；
 * 2. embedded occurrence 其次读取 asset source 保留的包内 assemblyNodeId；
 * 3. 普通后端节点仍以稳定 nodeKey 作为本期兼容身份。
 *
 * @param node 当前项目模型节点。
 * @returns 规范化后的稳定 assemblyNodeId。
 * @throws 当节点没有显式、来源或 nodeKey 可提供的稳定装配身份时。
 */
export function resolveProjectModelNodeAssemblyNodeId(node: ModelAssetNode): string {
  const assemblyNodeId = resolveExplicitProjectModelNodeRuntimeAssemblyNodeId(node)
    ?? resolveProjectModelNodeSourceAssemblyNodeId(node)
    ?? normalizeAssemblyNodeId(node.nodeKey.trim())
  if (!assemblyNodeId) {
    throw new Error(`PuppyCAD 项目节点缺少稳定装配身份：nodeKey=${node.nodeKey}`)
  }
  return assemblyNodeId
}

/**
 * 解析离线加载阶段补入的项目一级节点应使用的装配实例身份。
 *
 * 设计说明：
 * 1. 已提交节点由 `node_where` 恢复出的身份仍然优先，保证提交前后 occurrence 不漂移；
 * 2. `node_where` 若错误指向父装配根，只能说明该 direct child 尚未形成独立持久化记录，
 *    不能让父子节点共享同一个 `assemblyNodeId`；
 * 3. 此时仅在离线自动补入边界使用已经完成结构冲突校验的 `targetNodeKey` 首次分配路径式身份，
 *    与既有项目节点的装配路径格式保持一致；写入后立即冻结，下游禁止再次从结构键推导。
 *
 * @param input 当前父装配、待补入节点和可选的离线包恢复身份。
 * @returns 待写入新增一级节点的独立 `assemblyNodeId`。
 * @throws 恢复身份不可用且 `targetNodeKey` 为空，或路径身份仍与父装配冲突时抛出。
 */
export function resolveMissingFirstLevelNodeAssemblyNodeId(input: {
  /** 当前加载单元的父装配根节点。 */
  readonly parentNode: ModelAssetNode
  /** 自动补入事务已经确定且完成结构冲突校验的目标节点 key。 */
  readonly targetNodeKey: string
  /** 从当前仓库包 `node_where` 恢复出的候选 occurrence 身份。 */
  readonly recoveredAssemblyNodeId?: string | null
}): string {
  const parentAssemblyNodeId = resolveProjectModelNodeAssemblyNodeId(input.parentNode)
  const recoveredAssemblyNodeId = normalizeAssemblyNodeId(
    input.recoveredAssemblyNodeId?.trim() ?? '',
  )

  // 只有独立于父装配根的恢复结果才能代表当前 direct child 的既有 occurrence。
  if (recoveredAssemblyNodeId && recoveredAssemblyNodeId !== parentAssemblyNodeId) {
    return recoveredAssemblyNodeId
  }

  const targetAssemblyNodeId = normalizeAssemblyNodeId(input.targetNodeKey.trim())
  if (!targetAssemblyNodeId || targetAssemblyNodeId === parentAssemblyNodeId) {
    throw new Error(
      '离线自动补入一级节点缺少独立路径身份：'
      + `targetNodeKey=${input.targetNodeKey}, parentAssemblyNodeId=${parentAssemblyNodeId}`,
    )
  }

  return targetAssemblyNodeId
}

/**
 * 读取项目节点显式携带的运行时 occurrence 身份。
 *
 * @param node 当前项目模型节点。
 * @returns 规范化后的显式运行时身份；未声明时返回 null。
 */
export function resolveExplicitProjectModelNodeRuntimeAssemblyNodeId(
  node: ModelAssetNode,
): string | null {
  const assemblyNodeId = normalizeAssemblyNodeId(node.assemblyNodeId?.trim() ?? '')
  return assemblyNodeId || null
}

/**
 * 读取 embedded asset source 保留的包内装配身份。
 *
 * @param node 当前项目模型节点。
 * @returns 规范化后的包内 source 身份；普通节点返回 null。
 */
export function resolveProjectModelNodeSourceAssemblyNodeId(node: ModelAssetNode): string | null {
  const sourceAssemblyNodeId = node.assetSource?.kind === 'embedded-puppy-occurrence'
    ? node.assetSource.assemblyNodeId
    : ''
  const assemblyNodeId = normalizeAssemblyNodeId(sourceAssemblyNodeId.trim())
  return assemblyNodeId || null
}

/**
 * 把包内装配身份提升到当前项目 occurrence 挂载作用域。
 *
 * 该函数同时供项目树挂载与 imported hierarchy 组合使用，防止两条链路各自产生不同 ID。
 *
 * @param sourceAssemblyNodeId 包内或新加载源的装配身份。
 * @param mountAssemblyNodeId 当前项目 occurrence 的运行时挂载身份。
 * @returns 已落入当前挂载作用域的稳定运行时身份。
 */
export function resolvePackageHierarchyRuntimeAssemblyNodeId(
  sourceAssemblyNodeId: string,
  mountAssemblyNodeId: string,
): string {
  const sourceId = normalizeAssemblyNodeId(sourceAssemblyNodeId.trim())
  const mountId = normalizeAssemblyNodeId(mountAssemblyNodeId.trim())
  if (!sourceId) {
    return mountId
  }
  if (!mountId) {
    return sourceId
  }

  // runtime scope 必须精确保留实例索引；A[0] 与 A[1] 是两个不同 occurrence。
  const isAlreadyScoped = sourceId === mountId || sourceId.startsWith(`${mountId}/`)

  return isAlreadyScoped ? sourceId : `${mountId}/${sourceId}`
}

/**
 * 为旧数据缺失显式 bridge 的 occurrence 生成稳定且仅限运行期使用的身份。
 *
 * @param compatibilityBaseId 现有身份 resolver 提供的兼容基值。
 * @param occurrenceKey 稳定结构定位键；禁止使用遍历序号。
 * @returns 带明确 project occurrence 作用域标记的运行时身份。
 * @throws base 或 occurrence key 为空时抛出带输入上下文的错误。
 */
export function createProjectModelRuntimeOccurrenceAssemblyNodeId(
  compatibilityBaseId: string,
  occurrenceKey: string,
): string {
  const normalizedBaseId = normalizeAssemblyNodeId(compatibilityBaseId.trim())
  const normalizedOccurrenceKey = occurrenceKey.trim()
  if (!normalizedBaseId || !normalizedOccurrenceKey) {
    throw new Error(
      'PuppyCAD runtime occurrence ID 无效：'
      + `baseId=${normalizedBaseId || '<empty>'}, occurrenceKey=${normalizedOccurrenceKey || '<empty>'}`,
    )
  }

  return `${normalizedBaseId}${PROJECT_MODEL_RUNTIME_OCCURRENCE_SCOPE}${encodeURIComponent(normalizedOccurrenceKey)}`
}

/**
 * 统一解析项目节点挂载后的运行时 occurrence 身份。
 *
 * @param node 待解析的项目模型节点。
 * @param mountRuntimeAssemblyNodeId source-only 节点所属容器的运行时挂载身份。
 * @returns 显式 runtime、容器作用域 source 或稳定 nodeKey generator 结果。
 */
export function resolveMountedProjectModelNodeRuntimeAssemblyNodeId(
  node: ModelAssetNode,
  mountRuntimeAssemblyNodeId: string,
): string {
  const explicitRuntimeAssemblyNodeId = resolveExplicitProjectModelNodeRuntimeAssemblyNodeId(node)
  if (explicitRuntimeAssemblyNodeId) {
    // 显式 runtime 是运行树权威值，禁止重新 scope 或按其它字段消歧。
    return explicitRuntimeAssemblyNodeId
  }

  const sourceAssemblyNodeId = resolveProjectModelNodeSourceAssemblyNodeId(node)
  if (sourceAssemblyNodeId) {
    return resolvePackageHierarchyRuntimeAssemblyNodeId(
      sourceAssemblyNodeId,
      mountRuntimeAssemblyNodeId,
    )
  }

  return createProjectModelRuntimeOccurrenceAssemblyNodeId(node.nodeKey, node.nodeKey)
}

/**
 * 为项目模型树创建运行时装配身份表。
 *
 * 结构层保留 nodeKey；embedded source 使用其 container 的运行身份完成 scope。历史普通唯一节点
 * 保留 nodeKey 兼容值；任何最终 runtime ID 冲突都明确失败，禁止按实例、名称或遍历顺序自动消歧。
 *
 * @internal 仅供项目模型组合加载链路使用。
 * @param root 项目模型根节点。
 * @returns ModelAssetNode 对象到运行时装配节点 ID 的只读映射。
 */
export function createProjectModelRuntimeAssemblyNodeIds(
  root: ModelAssetNode,
): ReadonlyMap<ModelAssetNode, string> {
  const nodes: ModelAssetNode[] = []
  const nodesByNodeKey = new Map<string, ModelAssetNode[]>()
  const runtimeAssemblyNodeIds = new Map<ModelAssetNode, string>()
  const nodeByRuntimeAssemblyNodeId = new Map<string, ModelAssetNode>()
  const resolvingNodes = new Set<ModelAssetNode>()

  /** 按树顺序收集节点及结构 key 候选，不派生任何运行身份。 */
  const collect = (node: ModelAssetNode): void => {
    nodes.push(node)
    const sameKeyNodes = nodesByNodeKey.get(node.nodeKey) ?? []
    sameKeyNodes.push(node)
    nodesByNodeKey.set(node.nodeKey, sameKeyNodes)
    node.children.forEach(collect)
  }
  collect(root)

  /** 创建冲突和容器错误共用的稳定身份诊断文本。 */
  const describeIdentity = (node: ModelAssetNode): string => {
    const source = resolveProjectModelNodeSourceAssemblyNodeId(node) ?? '<none>'
    const container = node.assetSource?.kind === 'embedded-puppy-occurrence'
      ? node.assetSource.containerNodeKey
      : '<none>'
    return `nodeKey=${node.nodeKey}, source=${source}, container=${container}`
  }

  /** 递归解析单节点身份；容器可以晚于引用节点出现在树中。 */
  const resolveNode = (node: ModelAssetNode): string => {
    const resolved = runtimeAssemblyNodeIds.get(node)
    if (resolved) {
      return resolved
    }
    if (resolvingNodes.has(node)) {
      throw new Error(`PuppyCAD 项目 runtime assemblyNodeId 容器引用成环：${describeIdentity(node)}`)
    }
    resolvingNodes.add(node)

    const explicitRuntimeAssemblyNodeId = resolveExplicitProjectModelNodeRuntimeAssemblyNodeId(node)
    const sourceAssemblyNodeId = resolveProjectModelNodeSourceAssemblyNodeId(node)
    let runtimeAssemblyNodeId: string
    if (explicitRuntimeAssemblyNodeId) {
      runtimeAssemblyNodeId = resolveMountedProjectModelNodeRuntimeAssemblyNodeId(node, '')
    } else if (sourceAssemblyNodeId && node.assetSource?.kind === 'embedded-puppy-occurrence') {
      const containerNodes = nodesByNodeKey.get(node.assetSource.containerNodeKey) ?? []
      if (containerNodes.length !== 1) {
        throw new Error(
          `PuppyCAD 项目 runtime assemblyNodeId 容器无法唯一定位：${describeIdentity(node)}, candidates=${containerNodes.length}`,
        )
      }
      const containerNode = containerNodes[0]
      if (!containerNode) {
        throw new Error(`PuppyCAD 项目 runtime assemblyNodeId 容器解析失败：${describeIdentity(node)}`)
      }
      const containerRuntimeAssemblyNodeId = resolveNode(containerNode)
      runtimeAssemblyNodeId = resolveMountedProjectModelNodeRuntimeAssemblyNodeId(
        node,
        containerRuntimeAssemblyNodeId,
      )
    } else if ((nodesByNodeKey.get(node.nodeKey)?.length ?? 0) > 1) {
      // 重复结构 key 必须生成明确 runtime；相同结果随后由冲突检查拒绝，禁止顺序消歧。
      runtimeAssemblyNodeId = resolveMountedProjectModelNodeRuntimeAssemblyNodeId(node, '')
    } else {
      // 普通唯一节点保留 nodeKey 兼容值，避免改变既有 metadata 与持久化绑定合约。
      runtimeAssemblyNodeId = resolveProjectModelNodeAssemblyNodeId(node)
    }

    resolvingNodes.delete(node)
    const conflictingNode = nodeByRuntimeAssemblyNodeId.get(runtimeAssemblyNodeId)
    if (conflictingNode && conflictingNode !== node) {
      throw new Error(
        'PuppyCAD 项目 runtime assemblyNodeId 冲突：'
        + `runtimeAssemblyNodeId=${runtimeAssemblyNodeId}, first={${describeIdentity(conflictingNode)}}, `
        + `second={${describeIdentity(node)}}`,
      )
    }
    runtimeAssemblyNodeIds.set(node, runtimeAssemblyNodeId)
    nodeByRuntimeAssemblyNodeId.set(runtimeAssemblyNodeId, node)
    return runtimeAssemblyNodeId
  }

  nodes.forEach(resolveNode)
  return runtimeAssemblyNodeIds
}
